import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Loader2, Trash2, X, Video, Mail, Phone, Clock, MessageSquare, Users, HelpCircle } from 'lucide-react';

/* Match CalendarPage.js grid metrics so this looks like the recruiter calendar. */
const START_HOUR = 7;
const END_HOUR = 21;
const HOUR_HEIGHT = 48;
const SNAP_MIN = 30;
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const DEMO_COLOR = '#007BFF';

const fmtMins = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

/**
 * Admin "Demo Request Calendar" — full-page view in /internal (sidebar).
 * Visually matches the recruiter CalendarPage; shows booked demos + admin blocks, and lets
 * the admin drag-select empty time to block it (snaps to 30 min). Click a block to remove it.
 * (Built as its own component — the live recruiter CalendarPage.js is NOT modified.)
 */
export default function DemoCalendarView() {
  const [refDate, setRefDate] = useState(new Date());
  const [data, setData] = useState({ meetings: [], blocks: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(null); // { dayIdx, startMin, endMin, colEl }
  const [selected, setSelected] = useState(null); // booked demo whose details popup is open
  const [popupPos, setPopupPos] = useState(null);
  const gridRef = useRef(null);
  const popupRef = useRef(null);

  // Close the details popup on outside click / Escape (mirrors CalendarPage).
  useEffect(() => {
    if (!selected) return undefined;
    const onClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) setSelected(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [selected]);

  // Position the popup beside the clicked meeting, clamped to the viewport.
  const openMeetingPopup = (meeting, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const PW = 380;
    const PH = 440;
    let left = rect.right + 10;
    let top = rect.top;
    if (left + PW > window.innerWidth - 16) left = rect.left - PW - 10;
    if (left < 16) left = 16;
    if (top + PH > window.innerHeight - 16) top = window.innerHeight - PH - 16;
    if (top < 16) top = 16;
    setPopupPos({ top, left });
    setSelected(meeting);
  };

  const HEARD_LABELS = {
    referral: 'Referral',
    linkedin: 'LinkedIn',
    search: 'Search engine',
    event: 'Event / webinar',
    other: 'Other',
  };

  const weekStart = startOfWeek(refDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(refDate, { weekStartsOn: 0 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const fetchData = useCallback(() => {
    setLoading(true);
    axios
      .get('/api/internal/demo-calendar', {
        params: { from: weekStart.toISOString(), to: addDays(weekStart, 7).toISOString() },
      })
      .then((res) => setData({ meetings: res.data.meetings || [], blocks: res.data.blocks || [] }))
      .catch((err) => toast.error(err?.response?.data?.message || 'Failed to load calendar'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart.getTime()]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const yToMinutes = (clientY, colEl) => {
    const rect = colEl.getBoundingClientRect();
    const raw = START_HOUR * 60 + ((clientY - rect.top) / HOUR_HEIGHT) * 60;
    const snapped = Math.round(raw / SNAP_MIN) * SNAP_MIN;
    return Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, snapped));
  };

  const onColMouseDown = (e, dayIdx) => {
    if (e.button !== 0 || saving) return;
    const m = yToMinutes(e.clientY, e.currentTarget);
    setDrag({ dayIdx, startMin: m, endMin: m, colEl: e.currentTarget });
  };
  const onColMouseMove = (e) => {
    if (!drag) return;
    setDrag((d) => ({ ...d, endMin: yToMinutes(e.clientY, d.colEl) }));
  };
  const onMouseUp = async () => {
    if (!drag) return;
    const lo = Math.min(drag.startMin, drag.endMin);
    const hi = Math.max(drag.startMin, drag.endMin);
    const dayIdx = drag.dayIdx;
    setDrag(null);
    if (hi - lo < SNAP_MIN) return;

    const start = new Date(days[dayIdx]);
    start.setHours(Math.floor(lo / 60), lo % 60, 0, 0);
    const end = new Date(days[dayIdx]);
    end.setHours(Math.floor(hi / 60), hi % 60, 0, 0);

    setSaving(true);
    try {
      await axios.post('/api/internal/demo-blocks', {
        start: start.toISOString(),
        end: end.toISOString(),
      });
      toast.success('Time blocked');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not block time');
    } finally {
      setSaving(false);
    }
  };

  const deleteBlock = async (id) => {
    setSaving(true);
    try {
      await axios.delete(`/api/internal/demo-blocks/${id}`);
      toast.success('Block removed');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not remove block');
    } finally {
      setSaving(false);
    }
  };

  const pos = (startISO, endISO) => {
    const s = new Date(startISO);
    const e = new Date(endISO);
    const sMin = s.getHours() * 60 + s.getMinutes();
    const eMin = e.getHours() * 60 + e.getMinutes();
    return {
      top: ((sMin - START_HOUR * 60) / 60) * HOUR_HEIGHT,
      height: Math.max(18, ((eMin - sMin) / 60) * HOUR_HEIGHT),
    };
  };

  return (
    <div className="flex flex-col -mx-1" style={{ height: 'calc(100vh - 3rem)' }} onMouseUp={onMouseUp}>
      {/* Header */}
      <div className="shrink-0 mb-3">
        <h1 className="text-2xl font-bold text-gray-900">Demo Request Calendar</h1>
        <p className="mt-1 text-sm text-gray-500">
          Booked demos and blocked time.{' '}
          <span className="font-medium text-gray-700">Drag on an empty area to block</span> a time
          (hidden from the public booking page); click a block to remove it.
        </p>
      </div>

      {/* Toolbar (matches CalendarPage) */}
      <div className="flex items-center justify-between shrink-0 mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setRefDate(new Date())}
            className="h-9 px-4 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-50 hover:shadow-sm transition-all"
          >
            Today
          </button>
          <div className="flex items-center">
            <button onClick={() => setRefDate((d) => subWeeks(d, 1))} className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100" aria-label="Previous week">
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button onClick={() => setRefDate((d) => addWeeks(d, 1))} className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100" aria-label="Next week">
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
          <h2 className="text-xl font-normal text-slate-800 select-none">{format(weekStart, 'MMMM yyyy')}</h2>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DEMO_COLOR }} /> Demo</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Blocked</span>
          {(loading || saving) && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
      </div>

      {/* Calendar */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-0">
        {/* Day header row */}
        <div className="grid shrink-0 border-b border-slate-200" style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}>
          <div className="border-r border-slate-100" />
          {days.map((day) => {
            const today = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} className="py-2 text-center border-r border-slate-100 last:border-r-0">
                <div className={`text-[11px] font-semibold uppercase tracking-wider ${today ? 'text-[#1a73e8]' : 'text-slate-400'}`}>
                  {format(day, 'EEE')}
                </div>
                <div className="flex justify-center mt-0.5">
                  <span className={`text-[22px] font-normal leading-none w-10 h-10 flex items-center justify-center rounded-full ${today ? 'bg-[#1a73e8] text-white' : 'text-slate-800'}`}>
                    {format(day, 'd')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scrollable time grid */}
        <div ref={gridRef} className="flex-1 overflow-y-auto min-h-0">
          <div className="grid" style={{ gridTemplateColumns: '56px repeat(7, 1fr)', height: HOURS.length * HOUR_HEIGHT }}>
            {/* time labels */}
            <div className="relative border-r border-slate-100">
              {HOURS.map((h, i) => (
                <div key={h} className="absolute w-full pr-2 text-right" style={{ top: i * HOUR_HEIGHT - 7 }}>
                  <span className="text-[11px] text-slate-400 select-none">{format(new Date(2000, 0, 1, h), 'h a')}</span>
                </div>
              ))}
            </div>

            {/* day columns */}
            {days.map((day, dayIdx) => {
              const today = isSameDay(day, new Date());
              return (
                <div
                  key={day.toISOString()}
                  className={`relative border-r border-slate-100 last:border-r-0 cursor-crosshair select-none ${today ? 'bg-[#e8f0fe]/25' : ''}`}
                  onMouseDown={(e) => onColMouseDown(e, dayIdx)}
                  onMouseMove={onColMouseMove}
                >
                  {HOURS.map((h, i) => (
                    <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: i * HOUR_HEIGHT }} />
                  ))}

                  {/* drag preview */}
                  {drag && drag.dayIdx === dayIdx && (() => {
                    const lo = Math.min(drag.startMin, drag.endMin);
                    const hi = Math.max(drag.startMin, drag.endMin);
                    return (
                      <div className="absolute left-1 right-1 rounded-md bg-slate-400/30 border border-slate-400 z-20 flex items-center justify-center" style={{ top: ((lo - START_HOUR * 60) / 60) * HOUR_HEIGHT, height: Math.max(2, ((hi - lo) / 60) * HOUR_HEIGHT) }}>
                        <span className="text-[10px] font-medium text-slate-600">{fmtMins(lo)}–{fmtMins(hi)}</span>
                      </div>
                    );
                  })()}

                  {/* blocks */}
                  {data.blocks.filter((b) => isSameDay(new Date(b.startTime), day)).map((b) => {
                    const { top, height } = pos(b.startTime, b.endTime);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteBlock(b.id); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        title="Click to remove this block"
                        className="group absolute left-1 right-1 rounded-md bg-slate-300/70 border border-slate-400 z-10 flex items-center justify-center hover:bg-rose-100 hover:border-rose-300"
                        style={{ top, height }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-slate-500 group-hover:text-rose-500" />
                      </button>
                    );
                  })}

                  {/* booked demos */}
                  {data.meetings.filter((m) => isSameDay(new Date(m.startTime), day)).map((m) => {
                    const { top, height } = pos(m.startTime, m.endTime);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => openMeetingPopup(m, e)}
                        className="absolute left-1 right-1 rounded-md text-white z-10 px-1.5 py-1 overflow-hidden text-left cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/40"
                        style={{ top, height, backgroundColor: DEMO_COLOR }}
                      >
                        <div className="text-[10px] font-semibold leading-tight truncate">{m.name}</div>
                        {height > 30 && <div className="text-[9px] opacity-80 truncate">{m.company || m.email}</div>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Booked-demo details popup (requester's form details + booking info) */}
      {selected && popupPos && (
        <div
          ref={popupRef}
          className="fixed z-50 w-[380px] rounded-2xl bg-white overflow-hidden"
          style={{
            top: popupPos.top,
            left: popupPos.left,
            boxShadow:
              '0 24px 38px 3px rgba(0,0,0,.14), 0 9px 46px 8px rgba(0,0,0,.12), 0 11px 15px -7px rgba(0,0,0,.2)',
          }}
        >
          <div className="flex items-center justify-between px-5 pt-4">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[#007BFF]">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: DEMO_COLOR }} /> Demo request
            </span>
            <button onClick={() => setSelected(null)} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-5 pb-5 pt-1 max-h-[70vh] overflow-y-auto">
            <h3 className="text-[18px] font-semibold text-slate-900 leading-snug">{selected.name}</h3>
            {(selected.designation || selected.company) && (
              <p className="text-[13px] text-slate-500 mt-0.5">
                {[selected.designation, selected.company].filter(Boolean).join(' · ')}
              </p>
            )}

            <div className="mt-3 flex items-start gap-3 py-2.5 border-t border-slate-100">
              <Clock className="h-[18px] w-[18px] text-[#1a73e8] mt-0.5 shrink-0" />
              <p className="text-[13px] text-slate-700">
                {format(new Date(selected.startTime), 'EEEE, MMMM d')} · {format(new Date(selected.startTime), 'h:mm')} – {format(new Date(selected.endTime), 'h:mm a')}
              </p>
            </div>

            {selected.meetingLink && (
              <div className="flex items-start gap-3 py-2.5 border-t border-slate-100">
                <Video className="h-[18px] w-[18px] text-[#1a73e8] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <a href={selected.meetingLink} target="_blank" rel="noopener noreferrer" className="text-[13px] font-medium text-[#1a73e8] hover:underline">
                    Join with Google Meet
                  </a>
                  <p className="text-[11px] text-slate-400 truncate mt-0.5">{selected.meetingLink}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 py-2.5 border-t border-slate-100">
              <Mail className="h-[18px] w-[18px] text-slate-400 mt-0.5 shrink-0" />
              <a href={`mailto:${selected.email}`} className="text-[13px] text-slate-700 hover:underline break-all">{selected.email}</a>
            </div>

            {selected.phone && (
              <div className="flex items-start gap-3 py-2.5 border-t border-slate-100">
                <Phone className="h-[18px] w-[18px] text-slate-400 mt-0.5 shrink-0" />
                <a href={`tel:${selected.phone}`} className="text-[13px] text-slate-700 hover:underline">{selected.phone}</a>
              </div>
            )}

            {(selected.monthlyHires || selected.interests?.length > 0) && (
              <div className="flex items-start gap-3 py-2.5 border-t border-slate-100">
                <HelpCircle className="h-[18px] w-[18px] text-slate-400 mt-0.5 shrink-0" />
                <p className="text-[13px] text-slate-700">
                  {selected.monthlyHires && <>Hires <span className="font-medium">{selected.monthlyHires}</span> a month</>}
                  {selected.monthlyHires && selected.interests?.length > 0 && ' · '}
                  {selected.interests?.length > 0 && <>Wants to see <span className="font-medium">{selected.interests.join(', ')}</span></>}
                </p>
              </div>
            )}

            {selected.heardFrom && (
              <div className="flex items-start gap-3 py-2.5 border-t border-slate-100">
                <HelpCircle className="h-[18px] w-[18px] text-slate-400 mt-0.5 shrink-0" />
                <p className="text-[13px] text-slate-700">
                  Heard via <span className="font-medium">{HEARD_LABELS[selected.heardFrom] || selected.heardFrom}</span>
                </p>
              </div>
            )}

            {selected.message && (
              <div className="flex items-start gap-3 py-2.5 border-t border-slate-100">
                <MessageSquare className="h-[18px] w-[18px] text-slate-400 mt-0.5 shrink-0" />
                <p className="text-[13px] text-slate-600 whitespace-pre-line leading-relaxed">{selected.message}</p>
              </div>
            )}

            {selected.guests && selected.guests.length > 0 && (
              <div className="flex items-start gap-3 py-2.5 border-t border-slate-100">
                <Users className="h-[18px] w-[18px] text-slate-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[12px] font-medium text-slate-700 mb-1">Guests</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.guests.map((g) => (
                      <span key={g} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{g}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
